// Phase D — "Paste address → Maps link" helper.
//
// Tiny popover that takes a free-form address (e.g. "yard off N7 past Tesco")
// and inserts a Google-Maps-search URL into the surrounding notes textarea
// at the current caret position. The URL itself is built client-side:
//   https://www.google.com/maps/search/?api=1&query=<encoded address>
//
// On render in BookingDetail the existing `linkifyText` util (Phase 59.2 /
// Phase C) picks up the https:// prefix and renders it as a target=_blank
// anchor — no extra work needed at the display surface.
import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { MapPin } from "lucide-react";
import { buildMapsSearchUrl, insertAtCaret } from "@/components/MapsLinkHelper.helpers";

// Re-export the pure helpers so existing callers / tests can keep importing
// them from the component file if they want — they live in the .helpers.js
// sibling because jest in this repo doesn't resolve the `@/` alias.
export { buildMapsSearchUrl, insertAtCaret };

/**
 * Props:
 *   - textareaRef: React ref pointing at the notes <textarea>
 *   - value: current textarea value
 *   - onChange: (nextValue: string) => void — called once with the merged text
 *   - disabled?: bool — disables the button (e.g. read-only forms)
 *   - className?: optional className on the trigger button
 */
export default function MapsLinkHelper({ textareaRef, value, onChange, disabled = false, className = "" }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      // Defer focus so Radix mount finishes first.
      const t = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    } else {
      setDraft("");
    }
  }, [open]);

  const handleInsert = () => {
    const url = buildMapsSearchUrl(draft);
    if (!url) return;
    const ta = textareaRef?.current;
    const original = value ?? "";
    const selStart = ta && typeof ta.selectionStart === "number" ? ta.selectionStart : original.length;
    const selEnd   = ta && typeof ta.selectionEnd === "number"   ? ta.selectionEnd   : original.length;
    const { next, caret } = insertAtCaret(original, selStart, selEnd, url);
    onChange(next);
    setOpen(false);
    // Restore focus + caret position right after the inserted URL.
    setTimeout(() => {
      if (ta) {
        ta.focus();
        try { ta.setSelectionRange(caret, caret); } catch { /* ignore */ }
      }
    }, 0);
  };

  return (
    <Popover open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={disabled}
          className={`h-7 px-2 text-[11px] mono uppercase tracking-wider gap-1 text-muted-foreground hover:text-accent ${className}`}
          data-testid="maps-link-helper-open"
          title="Paste a rough address — we'll convert it to a clickable Google Maps link"
        >
          <MapPin size={12} strokeWidth={2.4} aria-hidden="true" />
          Paste address → Maps link
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[320px] p-3 space-y-3"
        data-testid="maps-link-helper-popover"
        align="end"
      >
        <div>
          <div className="text-xs font-extrabold uppercase tracking-wider text-muted-foreground">
            Address or landmark
          </div>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            Type how you&apos;d say it — Google Maps handles fuzzy queries. We
            insert the link at your cursor position.
          </p>
        </div>
        <Input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleInsert();
            } else if (e.key === "Escape") {
              e.preventDefault();
              setOpen(false);
            }
          }}
          placeholder="e.g. yard off N7 past Tesco, Naas"
          data-testid="maps-link-helper-input"
        />
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setOpen(false)}
            data-testid="maps-link-helper-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={handleInsert}
            disabled={!draft.trim()}
            data-testid="maps-link-helper-insert"
            className="bg-sky-500 hover:bg-sky-600 text-white"
          >
            Insert link
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
